/**
 * Every hour recorded, across every activity (M1 track D6).
 *
 * This was a work list: it defaulted to PENDING because what staff came here
 * to do was approve or reject. Approval was removed on 8 Oct 2026 and hours
 * are counted as they are recorded, so nothing waits and the default filter
 * would have shown an empty screen for ever.
 *
 * What it is now is the one place to look across activities - search, filter
 * by status, page through - where the activity screen only ever shows its own.
 * Recording still happens on the activity, where the roster is.
 */

import { useState } from 'react';
import { ATTENDANCE_STATUSES } from '@parinaam-vms/shared';
import {
  Card,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  MultiSelectInput,
  PageHeader,
  Pager,
  SelectInput,
  TextInput,
} from '../../components/common';
import { AttendanceTable } from '../../components/attendance/AttendanceTable';
import { CRUMB } from '../../constants/breadcrumbs';
import { ROWS_PER_PAGE } from '../../constants/pagination';
import { useAttendance } from '../../hooks/useAttendance';
import { useActivities, usePrograms } from '../../hooks/usePrograms';
import { useSessionList } from '../../hooks/useSessions';
import type { Session } from '../../services/api/sessions';
import { countedTitle, formatDate, humaniseCode } from '../../utils/format';

const STATUS_OPTIONS = ATTENDANCE_STATUSES.map((status) => ({
  value: status,
  label: humaniseCode(status),
}));

/**
 * An empty multi-select is no filter at all.
 *
 * Sending `[]` would be a filter that matches nothing, and the screen would
 * answer somebody who had just cleared a filter with an empty table. The API
 * reaches the same conclusion from the other side (`uuidList`); this stops the
 * empty array being sent in the first place, and keeps it out of the React
 * Query key, so clearing a filter returns to the cached unfiltered page rather
 * than refetching it.
 */
function atLeastOne(values: string[]): string[] | undefined {
  return values.length > 0 ? values : undefined;
}

export function AttendancePage() {
  /*
   * No status filter by default (8 Oct 2026).
   *
   * It opened on PENDING while this was an approval queue. Nothing is pending
   * any more, so that default would greet staff with an empty table on a screen
   * that holds every hour the organisation has recorded.
   */
  const [status, setStatus] = useState<string>('');

  /*
   * Three multi-selects (Parinaam, 9 Oct 2026).
   *
   * An empty array is "do not filter on this", not "match nothing" — the
   * question a coordinator asks is "the two school programmes", and one at a
   * time made them run the report twice and add it up. `uuidList` on the API
   * side turns an empty list back into an absent filter, so the two ends agree
   * about what empty means.
   */
  const [programIds, setProgramIds] = useState<string[]>([]);
  const [activityIds, setActivityIds] = useState<string[]>([]);
  const [sessionIds, setSessionIds] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const programs = usePrograms({ page_size: 100 });

  /*
   * The three narrow each other, which is what makes them usable.
   *
   * Each list is filtered by whatever is chosen above it: activities by the
   * program, sessions by both. Choosing nothing is a normal state, and then
   * each list is simply everything.
   *
   * The session filter was hidden until an activity had been chosen until
   * Parinaam asked for it on 9 Oct 2026. The reason it was hidden was real —
   * a flat list of every session in the organisation is a hundred names with
   * no context, two of them called "Session 1" — but that is an argument about
   * the LABELS, not about whether the control should exist. So it is always
   * here, and `sessionOption` below carries the activity name until the
   * activity filter makes it redundant.
   */
  const activities = useActivities({
    program_id: atLeastOne(programIds),
    page_size: 100,
  });
  /*
   * 100 is the API's cap (MAX_PAGE_SIZE), and the list is newest-first, so an
   * unnarrowed filter offers the hundred most recent sessions. That is the
   * right hundred — a coordinator filtering attendance is looking for last
   * Saturday — and choosing a program or an activity narrows it to something
   * exhaustive long before the cap matters.
   */
  const sessions = useSessionList({
    program_id: atLeastOne(programIds),
    activity_id: atLeastOne(activityIds),
    page_size: 100,
  });

  const query = useAttendance({
    status: status || undefined,
    program_id: atLeastOne(programIds),
    activity_id: atLeastOne(activityIds),
    session_id: atLeastOne(sessionIds),
    from: from || undefined,
    to: to || undefined,
    page,
    page_size: ROWS_PER_PAGE,
  });

  const rows = query.data?.items ?? [];

  /*
   * How a session reads in the filter.
   *
   * The date is always there: it is what tells two sessions of the same
   * activity apart, and it is how a coordinator thinks of the one they want
   * ("last Saturday"). The activity name leads, until the activity filter is
   * set and every remaining option would repeat it.
   */
  const sessionOption = (session: Session) => {
    const when = formatDate(session.start_date);
    // One activity chosen means every option would repeat its name. Two or
    // more, and the name is the thing telling them apart again.
    return activityIds.length === 1
      ? `${session.session_name} — ${when}`
      : `${session.activity_name}: ${session.session_name} — ${when}`;
  };

  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  /*
   * Changing a program clears the activity and the session under it; changing
   * an activity clears the session. Otherwise a stale pair silently filters to
   * nothing — an empty table and no visible reason for it, which reads as the
   * records having disappeared.
   */
  const chooseProgram = (value: string[]) => {
    setProgramIds(value);
    setActivityIds([]);
    setSessionIds([]);
    setPage(1);
  };

  const chooseActivity = (value: string[]) => {
    setActivityIds(value);
    setSessionIds([]);
    setPage(1);
  };

  const chooseSession = (value: string[]) => {
    setSessionIds(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader title="Attendance" breadcrumbs={[CRUMB.attendance]} />

      <Card title={countedTitle('Attendance records', query.data?.pagination?.total)}>
        <FilterBar label="Filter attendance">
          <SelectInput
            label="Status"
            placeholder="All statuses"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(event) => change(setStatus)(event.target.value)}
          />
          <MultiSelectInput
            label="Program"
            placeholder="All programs"
            options={(programs.data?.items ?? []).map((program) => ({
              value: program.program_id,
              label: program.name,
            }))}
            value={programIds}
            onChange={chooseProgram}
          />
          <MultiSelectInput
            label="Activity"
            placeholder="All activities"
            options={(activities.data?.items ?? []).map((activity) => ({
              value: activity.activity_id,
              label: activity.name,
            }))}
            value={activityIds}
            onChange={chooseActivity}
          />
          <MultiSelectInput
            label="Session"
            placeholder="All sessions"
            options={(sessions.data?.items ?? []).map((session) => ({
              value: session.session_id,
              label: sessionOption(session),
            }))}
            value={sessionIds}
            onChange={chooseSession}
          />
          <TextInput
            label="From"
            type="date"
            value={from}
            onChange={(event) => change(setFrom)(event.target.value)}
          />
          <TextInput
            label="To"
            type="date"
            value={to}
            onChange={(event) => change(setTo)(event.target.value)}
          />
        </FilterBar>

        {query.isPending ? (
          <LoadingState label="Loading attendance…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No attendance found"
            message={
              status
                ? 'No attendance record matches these filters.'
                : 'Hours are recorded on an activity, against the volunteers who gave them.'
            }
          />
        ) : (
          <>
            <Pager pagination={query.data?.pagination} onPageChange={setPage} />
            <AttendanceTable rows={rows} />
          </>
        )}
      </Card>
    </>
  );
}
